import { db } from './db';

const API_URL = import.meta.env.VITE_API_URL || '/backend/api';

const isOnline = () => window.navigator.onLine;

const handleResponse = async (response: Response) => {
    const text = await response.text();
    try {
        const data = JSON.parse(text);
        if (response.status === 401) {
            console.warn("Session expirée ou invalide. Déconnexion automatique...");
            localStorage.removeItem('atelier_bassoum_token');
            localStorage.removeItem('atelier_bassoum_user');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        if (!response.ok) {
            console.error("API Error Response:", data);
            throw new Error(data.message || data.error || 'Erreur API');
        }
        return data;
    } catch (err: any) {
        if (text.includes("<!DOCTYPE html>") || text.includes("<html>")) {
             console.error("HTML detected instead of JSON. Server Error Log excerpt:", text.substring(0, 200));
             throw new Error("Le serveur a renvoyé une page HTML au lieu de JSON. Vérifiez l'URL de l'API.");
        }
        console.error("Non-JSON Response or Parsing Error:", text);
        const excerpt = text.length > 100 ? text.substring(0, 100) + "..." : text;
        throw new Error(err.message || `Erreur Format Réponse (${response.status}): ${excerpt}`);
    }
};

// Queue helper
const queueAction = async (type: string, params: any) => {
  await db.actionsQueue.add({
    type,
    params,
    timestamp: Date.now()
  });
  console.log(`Action ${type} mise en file d'attente (Hors-ligne)`);
};

export const api = {
  // Generic GET
  get: async (endpoint: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    const headers: any = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${API_URL}${endpoint}`, { headers });
    return await handleResponse(response);
  },
  getTodayVentes: async (token: string) => {
    const response = await fetch(`${API_URL}/ventes`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Auth
  login: async (email: string, pin: string) => {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, pin }),
    });
    return await handleResponse(response);
  },
  getPublicUsers: async () => {
    // Forcer le non-cache pour mobile et s'assurer que l'URL est propre
    const url = `${API_URL}/auth/users?t=${Date.now()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    return await handleResponse(response);
  },

  // Commandes
  getCommandes: async (token: string) => {
    try {
      const response = await fetch(`${API_URL}/commandes`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await handleResponse(response);
      
      // Update cache atomically via transaction
      await db.transaction('rw', db.commandes, async () => {
          await db.commandes.clear();
          await db.commandes.bulkPut(data);
      });
      
      return data;
    } catch (err) {
      if (!isOnline()) {
        console.log("Offline: Récupération des commandes depuis le cache local.");
        return await db.commandes.toArray();
      }
      throw err;
    }
  },

  getCommande: async (token: string, id: string) => {
    try {
        const response = await fetch(`${API_URL}/commandes/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        return await handleResponse(response);
    } catch (err) {
        if (!isOnline()) {
            return await db.commandes.get(parseInt(id));
        }
        throw err;
    }
  },

  createCommande: async (token: string, commandeData: any) => {
    if (!isOnline()) {
      await queueAction('createCommande', { token, commandeData });
      return { message: "Commande enregistrée localement (Hors-ligne)", offline: true };
    }
    const response = await fetch(`${API_URL}/commandes`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify(commandeData),
    });
    return await handleResponse(response);
  },
  
  updateCommande: async (token: string, id: string, data: any) => {
    const response = await fetch(`${API_URL}/commandes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  softDeleteCommande: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/commandes/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  cancelCommande: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/commandes/${id}/cancel`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getTrashCommandes: async (token: string) => {
    const response = await fetch(`${API_URL}/commandes/trash`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  restoreCommande: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/commandes/${id}/restore`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  hardDeleteCommande: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/commandes/${id}/hard`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  updateCommandeStatus: async (token: string, id: string, statut: string, commentaire: string = "") => {
    const response = await fetch(`${API_URL}/commandes/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ statut, commentaire })
    });
    return await handleResponse(response);
  },
  
  revertCommandeStatus: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/commandes/${id}/revert`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  assignPersonnel: async (token: string, id: string, type: string, personnel_id: number) => {
    const response = await fetch(`${API_URL}/commandes/${id}/assign`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ type, personnel_id })
    });
    return await handleResponse(response);
  },

  updateCommandeTissu: async (token: string, id: string, tissuData: any) => {
    const response = await fetch(`${API_URL}/commandes/${id}/tissu`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(tissuData)
    });
    return await handleResponse(response);
  },

  getValidationHistory: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/validations?commande_id=${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getCommandePaiements: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/commandes/${id}/paiements`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  addCommandePaiement: async (token: string, id: string | number, data: { montant: number, mode_paiement: string, notes?: string }) => {
    if (!isOnline()) {
      await queueAction('addCommandePaiement', { token, id, data });
      return { message: "Paiement enregistré localement", offline: true };
    }
    const response = await fetch(`${API_URL}/commandes/${id}/paiements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  // Stocks
  getStocks: async (token: string) => {
    try {
      const response = await fetch(`${API_URL}/tissus?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        },
      });
      const data = await handleResponse(response);
      
      // Update cache atomically via transaction
      await db.transaction('rw', db.stocks, async () => {
          await db.stocks.clear();
          await db.stocks.bulkPut(data);
      });
      
      return data;
    } catch (err) {
      if (!isOnline()) return await db.stocks.toArray();
      throw err;
    }
  },

  createStock: async (token: string, stockData: any) => {
    if (!isOnline()) {
      await queueAction('createStock', { token, stockData });
      return { message: "Création stock enregistrée localement", offline: true };
    }
    const response = await fetch(`${API_URL}/tissus`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(stockData),
    });
    return await handleResponse(response);
  },

  updateStock: async (token: string, id: string, stockData: any) => {
    if (!isOnline()) {
      await queueAction('updateStock', { token, id, stockData });
      return { message: "Mise à jour stock enregistrée localement", offline: true };
    }
    const response = await fetch(`${API_URL}/tissus/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(stockData),
    });
    return await handleResponse(response);
  },

  softDeleteStock: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/tissus/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getTrashStocks: async (token: string) => {
    const response = await fetch(`${API_URL}/tissus/trash`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  restoreStock: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/tissus/${id}/restore`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  hardDeleteStock: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/tissus/${id}/hard`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Clients
  getClients: async (token: string) => {
    try {
      const response = await fetch(`${API_URL}/clients`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await handleResponse(response);
      
      // Update cache atomically via transaction
      await db.transaction('rw', db.clients, async () => {
          await db.clients.clear();
          await db.clients.bulkPut(data);
      });
      
      return data;
    } catch (err) {
      if (!isOnline()) return await db.clients.toArray();
      throw err;
    }
  },

  createClient: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/clients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  getClientHistory: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/clients/${id}/history`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  
  softDeleteClient: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/clients/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getTrashClients: async (token: string) => {
    const response = await fetch(`${API_URL}/clients/trash`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  restoreClient: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/clients/${id}/restore`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  hardDeleteClient: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/clients/${id}/hard`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Modèles
  getModeles: async (token: string) => {
    const response = await fetch(`${API_URL}/modeles`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },

  createModele: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/modeles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  updateModele: async (token: string, id: string, data: any) => {
    const response = await fetch(`${API_URL}/modeles/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  // Ventes
  getVentesToday: async (token: string) => {
    const response = await fetch(`${API_URL}/ventes`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },
  
  // Personnel
  getPersonnel: async (token: string) => {
    const response = await fetch(`${API_URL}/personnel`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },

  // Rapports
  getGlobalStats: async (token: string) => {
    const response = await fetch(`${API_URL}/rapports/stats`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },
  getCommandesReport: async (token: string, debut: string, fin: string) => {
    const response = await fetch(`${API_URL}/rapports/commandes?debut=${debut}&fin=${fin}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },
  getVentesReport: async (token: string, debut: string, fin: string) => {
    const response = await fetch(`${API_URL}/rapports/ventes?debut=${debut}&fin=${fin}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },

  // Utilisateurs (User Management)
  getUsers: async (token: string) => {
    const response = await fetch(`${API_URL}/utilisateurs`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },
  createUser: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/utilisateurs`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  updateUser: async (token: string, id: string | number, data: any) => {
    const response = await fetch(`${API_URL}/utilisateurs/${id}`, {
      method: 'PUT',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  deleteUser: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/utilisateurs/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  getTrashUsers: async (token: string) => {
    const response = await fetch(`${API_URL}/utilisateurs/trash`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  restoreUser: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/utilisateurs/${id}/restore`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  hardDeleteUser: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/utilisateurs/${id}/hard`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getPaieStats: async (token: string, debut: string, fin: string) => {
    const response = await fetch(`${API_URL}/paie?debut=${debut}&fin=${fin}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },

  getPaieHistory: async (token: string) => {
    const response = await fetch(`${API_URL}/paie/historique`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },

  savePaieRecord: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/paie`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  deletePaieRecord: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/paie/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  createVente: async (token: string, data: any) => {
    if (!isOnline()) {
      await queueAction('createVente', { token, venteData: data });
      return { message: "Vente enregistrée localement", offline: true };
    }
    const response = await fetch(`${API_URL}/ventes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  updateVentePaiement: async (token: string, id: number, data: any) => {
    const response = await fetch(`${API_URL}/ventes/${id}/paiement`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  uploadFacturePdf: async (token: string, blob: Blob, filename: string) => {
    const formData = new FormData();
    formData.append('pdf', blob, filename);
    const response = await fetch(`${API_URL}/upload-pdf`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return await handleResponse(response);
  },

  // --- UPLOADS ---
  uploadImage: async (token: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await fetch(`${API_URL}/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return await handleResponse(response);
  },

  // --- MESURES ---
  getMesureChamps: async (token: string) => {
    const response = await fetch(`${API_URL}/mesures/champs`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getClientProfils: async (token: string) => {
    const response = await fetch(`${API_URL}/mesures/profils`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  saveClientMesures: async (token: string, client_id: number, mesures: any[], notes: string = "") => {
    const response = await fetch(`${API_URL}/mesures/${client_id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ mesures, notes }),
    });
    return await handleResponse(response);
  },

  getClientMesures: async (token: string, client_id: number) => {
    const response = await fetch(`${API_URL}/mesures/${client_id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  deleteClientMesures: async (token: string, client_id: number) => {
    const response = await fetch(`${API_URL}/mesures/${client_id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },

  // Settings
  getSettings: async (token: string) => {
    const response = await fetch(`${API_URL}/settings`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  updateSettings: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  // Finance & Trésorerie
  getFinances: async (token: string, filters: any = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await fetch(`${API_URL}/finances?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  getFinanceStats: async (token: string) => {
    const response = await fetch(`${API_URL}/finances/stats`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  getFinanceCategories: async (token: string) => {
    const response = await fetch(`${API_URL}/finances/categories`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  addFinance: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/finances`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  deleteFinance: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/finances/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Personnel Management
  updatePersonnel: async (token: string, id: number, data: any) => {
    const response = await fetch(`${API_URL}/personnel/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  
  softDeletePersonnel: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/personnel/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getTrashPersonnel: async (token: string) => {
    const response = await fetch(`${API_URL}/personnel/trash`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  restorePersonnel: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/personnel/${id}/restore`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  hardDeletePersonnel: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/personnel/${id}/hard`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  createPersonnel: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/personnel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  // Mesure Champs Management
  saveMesureChamp: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/mesures/champs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  deleteMesureChamp: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/mesures/champs?id=${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  saveMesureChampsOrder: async (token: string, data: {id: number, ordre: number}[]) => {
    const response = await fetch(`${API_URL}/mesures/champs/order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  // Habit Types Management
  getHabitTypes: async (token: string) => {
    const response = await fetch(`${API_URL}/habits`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  saveHabitType: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/habits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  deleteHabitType: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/habits/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Helper for CORS-friendly image URLs
  getProxyUrl: (url: string | null | undefined) => {
    if (!url) return '';
    // Extraire seulement le nom du fichier, qu'il provienne d'une URL absolue, relative ou juste le nom brut
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    return `${API_URL}/proxy/${filename}`;
  },

  // Fournisseurs
  getFournisseurs: async (token: string) => {
    const response = await fetch(`${API_URL}/fournisseurs`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  createFournisseur: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  getFournisseurLots: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/fournisseurs/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  createFournisseurLot: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  getLotDetails: async (token: string, lotId: string | number) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots/${lotId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  addLotArticle: async (token: string, lotId: string | number, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots/${lotId}/articles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  addLotPaiement: async (token: string, lotId: string | number, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots/${lotId}/paiements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  updateArticleStock: async (token: string, articleId: string | number, decrement: number) => {
    const response = await fetch(`${API_URL}/articles/${articleId}/update-stock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ decrement })
    });
    return await handleResponse(response);
  },
  updateLotStatus: async (token: string, lotId: string | number, statut: string) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots/${lotId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ statut })
    });
    return await handleResponse(response);
  },
  deleteFournisseur: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/fournisseurs/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  updateLot: async (token: string, lotId: string | number, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots/${lotId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  deleteLot: async (token: string, supplierId: string | number, lotId: string | number) => {
    const response = await fetch(`${API_URL}/fournisseurs/${supplierId}/${lotId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getPaymentModes: async (token: string) => {
    const response = await fetch(`${API_URL}/fournisseurs/modes-paiement`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  createPaymentMode: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs/modes-paiement`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  deletePaymentMode: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/fournisseurs/modes-paiement/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Permissions
  getPermissions: async (token: string) => {
    const response = await fetch(`${API_URL}/permissions`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  getUserPermissions: async (token: string, userId: string | number) => {
    const response = await fetch(`${API_URL}/permissions/user/${userId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  updateUserPermissions: async (token: string, userId: string | number, permissions: number[]) => {
    const response = await fetch(`${API_URL}/permissions/assign/${userId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ permissions })
    });
    return await handleResponse(response);
  },

  getRolePermissions: async (token: string) => {
    const response = await fetch(`${API_URL}/permissions/matrix`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  updateRolePermissions: async (token: string, matrix: any) => {
    const response = await fetch(`${API_URL}/permissions/matrix`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ matrix })
    });
    return await handleResponse(response);
  },

  getMyPermissions: async (token: string) => {
    const response = await fetch(`${API_URL}/permissions/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  API_URL, // Expose for components that need it for manual fetch
};
